import { writable, get, derived } from 'svelte/store';
import { PRConnect } from "@projectroom/ui-lib";

const store = writable();

let authType, authSelectFlag, tutorialFlag;
let userInfo = {};

class User {

	// 두 종류의 데이터가 있음. 권한에 따라 달라지는 데이터, 사용여부 데이터(물론 권한에 따라서 변경을 할 수 있고 없고가 결정됨)
	constructor({ emailId, authType, price, authSelectFlag = false, tutorialFlag, botCnt, storyCnt, imageCnt, aiImageCnt, fontType, buttonType, tabType, availableType, quotaPolicy, minimumAuthType, isAvailableFlag, creepongId }) {
		this.emailId = emailId;
		this.authType = authType;
		this.price = price;
		this.authSelectFlag = authSelectFlag;
		this.tutorialFlag = tutorialFlag == 'N' ? true : false;
		this.botCnt = Number(botCnt);
		this.storyCnt = Number(storyCnt);
		this.imageCnt = Number(imageCnt);
		this.aiImageCnt = Number(aiImageCnt);
		this.fontType = fontType;
		this.buttonType = buttonType;
		this.tabType = tabType;
		this.availableType = availableType;
		this.quotaPolicy = quotaPolicy;
		this.minimumAuthType = minimumAuthType;
		this.isAvailableFlag = isAvailableFlag;
		this.creepongId = creepongId;
	}

	isLoggedIn() {
		return !!this.emailId; // emailId가 존재하면 로그인된 것으로 간주
	}

	setTutorialFlag(tutorialFlag) {
		this.tutorialFlag = tutorialFlag;
	}

	checkAuthority(target) {
		let result = { "allow": false, "minimumAuthType": "" };
		if (this[target]) {
			result.allow = true;
		} else {
			result.allow = false;
			for (const policy of this.quotaPolicy) {
				if (policy[target] == 'Y') {
					result.minimumAuthType = policy.authType;
					break;
				}
			}
		}
		return result;
	}

	checkAuthorityCnt(target, cnt) {
		let result = { "allow": false, "minimumAuthType": "" };
		if (this[target] >= cnt) {
			result.allow = true;
		} else {
			result.allow = false;
			for (const policy of this.quotaPolicy) {
				if (Number(policy[target]) >= cnt) {
					result.minimumAuthType = policy.authType;
					break;
				}
			}
		}
		return result;
	}

	includesType(target, context) {
		return this[target].includes(context);
	}

	checkAuthorityType(target, context) {
		let result = { "allow": false, "minimumAuthType": "" };
		if (this.includesType(target, context)) {
			result.allow = true;
		} else {
			for (const policy of this.quotaPolicy) {
				if (policy[target].includes(context)) {
					result.minimumAuthType = policy.authType;
					break;
				}
			}
		}
		return result;
	}

	isCreepong() {
		return this.creepongId ? true : false;
	}

	// 요금제 등급이 PRO 이상인지(PRO/ALL_IN_ONE/ENTERPRISE). 음성 채팅 등 상위 요금제 전용 기능 게이팅용.
	// ADMIN(스태프/슈퍼어드민 계정)은 전권 — 항상 허용(관리자 계정으로 테스트 시 잠기던 문제 방지).
	isProOrAbove() {
		return ["ADMIN", "PRO", "ALL_IN_ONE", "ENTERPRISE"].includes(this.authType);
	}
}

// const initialize = (flag) => {
//     const planInfo = get(store);
//     if (!planInfo || flag) {
//         PRConnect.send("/api/User/getUserAuthById", {}, data => {
//             if (data.header.status === "success") {
// 				userInfo = new User({...data.result.userAuth, quotaPolicy: data.result.quotaPolicy});
// 				store.set(userInfo);
//             }
//         });
//     }
// }

function fetchUserAuth() {
	return new Promise((resolve, reject) => {
		PRConnect.send("/api/User/getUserAuthById", {}, data => {
			if (data.header.status === "success") {
				const user = new User({
					...data.result.userAuth,
					quotaPolicy: data.result.quotaPolicy
				});
				store.set(user);
				resolve(user);
			} else {
				reject(new Error(data.header.message));
			}
		});
	});
}

let initPromise = null;

async function initialize(force = false) {
	const planInfo = get(store);
	if (!planInfo || force) {
		if (!initPromise) {
			initPromise = fetchUserAuth().finally(() => { initPromise = null; });
		}
		await initPromise;
	}
}

function createStore() {
	let initialized = false;

	// 화살표 함수 메서드에서는 this가 반환 객체를 가리키지 않으므로(모듈 최상위 this === undefined)
	// set을 지역 함수로 정의해 내부에서 직접 참조한다.
	// 또한 fetchUserAuth는 store.set(user)만 호출하고 모듈 userInfo는 갱신하지 않으므로,
	// 현재 store 값을 기준으로 병합해야 기존 사용자 데이터가 유실되지 않는다.
	const setUser = (message) => {
		const current = get(store) || userInfo;
		userInfo = new User({ ...current, ...message });
		store.set(userInfo);
	};

	return {
		// 구독이 최초로 이뤄지는 순간만 initialize() 호출
		subscribe(run) {
			const unsub = store.subscribe(run);
			if (!initialized) {
				initialized = true;
				initialize().catch(() => { /* 이미 내부에서 에러 로깅 */ });
			}
			return unsub;
		},

		set: setUser,
		update: (message) => {
			store.update(message);
		},

		updateAuth: (authType, authSelectFlag) => {
			PRConnect.send("/api/User/modifyUserAuthType", { authType }, data => {
				if (data.header.status === "success") {
					setUser({ ...data.result.userAuth, quotaPolicy: data.result.quotaPolicy, authSelectFlag });
				}
			});
		},

		get: () => {
			return get(store);
		},

		isLoggedIn: () => {
			const user = get(store);
			if (!user) {
				return false;
			}
			return user.isLoggedIn();
		},

		init: async (force = false) => {
			await initialize(force);
			// initialize가 끝난 다음 호출자에게 user가 세팅된 시점을 보장
			return get(store);
		},

		setMinimumAuth: (minimumAuth) => {
			setUser({ minimumAuth });
		},
		setTutorialFlag: (tutorialFlag) => {
			if (userInfo.tutorialFlag !== tutorialFlag) {
				PRConnect.send("/api/User/modifyTutorialFlag", {}, data => {
					if (data.header.status === "success") {
						setUser({ tutorialFlag: true });
					}
				});
			}
		},
		setAuthSelectFlag: (authSelectFlag) => {
			const current = get(store);
			current.authSelectFlag = authSelectFlag;
			store.set(current);
		},
		setAuthSelectFlagAndAvailableFlag: (authSelectFlag, isAvailableFlag) => {
			const current = get(store);
			setUser({ ...current, authSelectFlag, isAvailableFlag });
		},
		setIsAvailableFlag: (isAvailableFlag) => {
			const current = get(store);
			setUser({ ...current, isAvailableFlag });
		},
		getTutorialFlag: () => {
			let { tutorialFlag } = get(store);
			return tutorialFlag;
		},
		getCreepongId: () => {
			let { creepongId } = get(store);
			return creepongId;
		},
		isAvailableType: (target, context) => {
			let userInfo = get(store);
			return userInfo.includesType(target, context);
		},
		isAvailableAuthority: (target) => {
			let userInfo = get(store);
			return userInfo[target];
		}
		,
		isAuthority: (target, context) => {
			let userInfo = get(store);
			let result = {};
			if (typeof userInfo[target] === "number") {
				result = userInfo.checkAuthorityCnt(target, context);
			} else if (typeof userInfo[target] === "boolean") {
				result = userInfo.checkAuthority(target);
			} else {
				result = userInfo.checkAuthorityType(target, context);
			}

			if (result.allow) {
				return true;
			}
			setUser({ minimumAuthType: result.minimumAuthType, isAvailableFlag: true });
			return false;
		}
		,
		isCreepong: () => {
			let userInfo = get(store);
			if (!userInfo) {
				return false; // userInfo가 없으면 기본적으로 false를 반환
			}
			return userInfo.isCreepong();
		}
		,
		// PRO 이상 요금제 여부. 로그인/로드 전이면 false(잠금 유지 — 게이팅은 보수적으로).
		isProOrAbove: () => {
			let userInfo = get(store);
			if (!userInfo) {
				return false;
			}
			return userInfo.isProOrAbove();
		}
		,
		setInitializeFlag: () => {
			initialized = false;
		}
	}
}

export const planStore = createStore();
export const isCreepong = derived(planStore, $u => {
	return $u?.isCreepong() ?? false
});